import React, { useState } from "react";
import { triageResearchPulseCluster } from "../../../Services/adminResearchPulse";
import { Button } from "../../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Textarea } from "../../../ui/textarea";
import {
  STATUSES,
  TRACE_STAGES,
  errorMessage,
  statusMeta,
} from "./researchPulseFormat";

const NOTE_MAX = 2000;
const LINKED_MAX = 64;
const TRACE_REF_MAX = 200;

const nativeSelect =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Triage one cluster. core-lambda records who triaged it; saving never creates,
 * certifies or publishes a Dialogue. The parent keys this form by the cluster's
 * status + linked id, so a saved triage remounts it with the new values.
 */
const TriageForm = ({ cluster, onSaved }) => {
  const [status, setStatus] = useState(cluster?.status || "NEW");
  const [note, setNote] = useState("");
  const [linkedId, setLinkedId] = useState(cluster?.linked_dialogue_id || "");
  const [traceStage, setTraceStage] = useState("");
  const [traceRef, setTraceRef] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const hint = statusMeta(status).hint;

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const row = await triageResearchPulseCluster(cluster.cluster_id, {
        status,
        note,
        linked_dialogue_id: linkedId,
        trace_stage: traceStage || undefined,
        trace_ref: traceStage ? traceRef : undefined,
      });
      setNote("");
      setTraceStage("");
      setTraceRef("");
      if (onSaved) await onSaved(row);
    } catch (err) {
      setError(errorMessage(err, "Failed to save the triage"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Triage</CardTitle>
        <p className="text-xs text-muted-foreground">
          Records a research decision on this need. It never creates, certifies or
          publishes a Dialogue.
        </p>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={submit}>
          <div className="space-y-1.5">
            <Label htmlFor="triage-status">Status</Label>
            <select
              id="triage-status"
              className={nativeSelect}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {statusMeta(s).label}
                </option>
              ))}
            </select>
            {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="triage-note">Note (optional)</Label>
            <Textarea
              id="triage-note"
              rows={3}
              maxLength={NOTE_MAX}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why this decision?"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="triage-linked">Linked Dialogue id (optional)</Label>
            <Input
              id="triage-linked"
              maxLength={LINKED_MAX}
              value={linkedId}
              onChange={(e) => setLinkedId(e.target.value)}
              placeholder="CDLG-00000"
            />
            <p className="text-xs text-muted-foreground">
              The canonical Dialogue that covers or was authored for this need.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="triage-trace-stage">Trace stage (optional)</Label>
              <select
                id="triage-trace-stage"
                className={nativeSelect}
                value={traceStage}
                onChange={(e) => setTraceStage(e.target.value)}
              >
                <option value="">None</option>
                {TRACE_STAGES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="triage-trace-ref">Trace reference</Label>
              <Input
                id="triage-trace-ref"
                maxLength={TRACE_REF_MAX}
                value={traceRef}
                disabled={!traceStage}
                onChange={(e) => setTraceRef(e.target.value)}
                placeholder="e.g. QA-123 or v26.9"
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <Button type="submit" disabled={saving} className="w-full">
            {saving ? "Saving…" : "Save triage"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default TriageForm;
